import React from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Menu, MenuItem, Avatar, Tooltip, Divider, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationMenu from './NotificationMenu';

const Navbar = ({ toggleDrawer, onToggleSidebar, sidebarCollapsed }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  return (
    <AppBar position="fixed" elevation={0} className="transition-all" sx={{ zIndex: 1200 }}>
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '72px', px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={isMobile ? "Toggle mobile menu" : (sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar")}>
            <IconButton
              color="inherit"
              aria-label={isMobile ? "toggle mobile drawer" : "toggle sidebar"}
              edge="start"
              onClick={isMobile ? toggleDrawer : onToggleSidebar}
              sx={{ mr: 2 }}
              className="menu-button"
            >
              <MenuIcon className="transition-colors" />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WhatsAppIcon sx={{ color: '#25D366', mr: 1.5, fontSize: 32 }} className="transition-colors" />
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, letterSpacing: '0.5px', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              WhatsApp API Portal
            </Typography>
          </Box>
        </Box>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Help & Resources">
              <IconButton
                color="inherit"
                className="hover-scale transition-all"
                sx={{ mx: 1, borderRadius: 2 }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            <NotificationMenu />

            <Tooltip title="Account settings">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                className="hover-scale transition-all"
                sx={{ ml: 1, borderRadius: 2 }}
              >
                {user.name ? (
                  <Avatar className="transition-all" sx={{ bgcolor: '#128C7E', width: 40, height: 40, border: '2px solid #ffffff', fontWeight: 600 }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            </Tooltip>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              className="transition-all"
              sx={{ 
                mt: 1.5,
                '& .MuiPaper-root': {
                  borderRadius: 3,
                  minWidth: 200,
                  boxShadow: 'var(--shadow-3)'
                }
              }}
            >
              <Box sx={{ px: 3, py: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  {user.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {user.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleProfile} className="transition-colors hover-lift" sx={{ py: 1.5, px: 3, borderRadius: 2, mx: 1, my: 0.5 }}>Profile Settings</MenuItem>
              <MenuItem onClick={handleLogout} className="transition-colors hover-lift" sx={{ py: 1.5, px: 3, borderRadius: 2, mx: 1, my: 0.5, color: 'error.main' }}>Logout</MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
